import { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { ESPatientService } from '@/services/es/esPatientService.ts';
import { transferWebSocketService } from '@/services/api';
import { formatAppointmentDateTime } from '@/utils/timezoneUtils.ts';
import { loadGroupSelection, STORAGE_KEY, saveLockedPatient } from '@/hooks/es/useESPatientData.ts';
import { patientsApi } from '@/services/api/patients';

export const useESPatientOperations = (esService: ESPatientService | null) => {
  const { user, token, hasuraToken } = useAuth();
  const { toast } = useToast();

  // Form state
  const [selectedCCMStatus, setSelectedCCMStatus] = useState('');
  const [comment, setComment] = useState('');
  const [appointmentDateTime, setAppointmentDateTime] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedWorkflowStatusId, setSelectedWorkflowStatusId] = useState<number | null>(null);

  // Button states
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDemographic, setIsSavingDemographic] = useState(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
  const [transferButtonDisabled, setTransferButtonDisabled] = useState(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const [isPatientSaved, setIsPatientSaved] = useState(false);
  const [isFormLocked, setIsFormLocked] = useState(false);

  // Group validation state
  const [storedGroupInvalid, setStoredGroupInvalid] = useState(false);
  const [invalidGroupName, setInvalidGroupName] = useState('');

  // Transfer state
  const [showTransferStatus, setShowTransferStatus] = useState(false);
  const [transferState, setTransferState] = useState<'idle' | 'waiting' | 'accepted'>('idle');
  const [transferId, setTransferId] = useState<number | null>(null);
  const [careSpecialist, setCareSpecialist] = useState<string>('');
  const [transferCompleted, setTransferCompleted] = useState(false);
  const [isTransferInProgress, setIsTransferInProgress] = useState(false);
  const [transferTimeout, setTransferTimeout] = useState<NodeJS.Timeout | null>(null);
  const transferTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // SMS Dialog state
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('');
  const [selectedPhoneType, setSelectedPhoneType] = useState<'mobile' | 'home'>('mobile');

  // Group completion dialog state
  const [showGroupCompletionDialog, setShowGroupCompletionDialog] = useState(false);
  const [currentGroupName, setCurrentGroupName] = useState('');
  const [nextGroupName, setNextGroupName] = useState('');

  // Handle CCM status change
  const handleCCMStatusChange = (value: string) => {
    setSelectedCCMStatus(value);
    setHasUnsavedChanges(true);
    // Enable Transfer Patient button only when CCM status is "Consent"
    setTransferButtonDisabled(value !== 'Consent');
  };

  // Handle comment change
  const handleCommentChange = (value: string) => {
    setComment(value);
    setHasUnsavedChanges(true);
  };

  // Handle save patient data
  // Appointment data interface for save
  interface AppointmentDataForSave {
    appointmentType: 'virtual' | 'in-person' | null;
    resourceId?: number;
    facilityId?: number;
    date: string;
    startTime: string;
    endTime: string;
    availabilityDayTimeId: number;
    timezone?: string;
  }

  const handleSave = async (
    patientData: any,
    patientClinicDataId: number,
    groupId: number,
    ccmStatusOptions: any[],
    editableFields: any,
    refetchPatientData: () => Promise<void>,
    statusToIdMapping: Record<string, number>,
    appointmentId: number | null = null,
    selectedAppointmentDate?: string | null,
    workflowStatusId?: number | null,
    selectedStatusesByProgramType?: Record<string, string>,
    programTypeStatuses?: Array<{ program_type: string; program_type_id?: number; program_status: string; program_status_id: number }>,
    externalWorkflowId?: number | null,
    appointmentData?: AppointmentDataForSave | null,
    assignmentData?: any
  ) => {
    // Validate required fields
    if (!comment.trim()) {
      toast({
        title: "Validation Error",
        description: "Comments are required",
        variant: "destructive"
      });
      return;
    }

    if (!workflowStatusId) {
      toast({
        title: "Validation Error",
        description: "Please select a workflow status",
        variant: "destructive"
      });
      return;
    }

    if (!patientData || !user?.id || !token || !esService) {
      toast({
        title: "Error",
        description: "Missing required data",
        variant: "destructive"
      });
      return;
    }

    // Validate appointment data if provided
    if (appointmentData) {
      if (appointmentData.appointmentType === 'virtual' && !appointmentData.resourceId) {
        toast({
          title: "Validation Error",
          description: "Resource/Provider is required for virtual appointments",
          variant: "destructive"
        });
        return;
      }
      if (appointmentData.startTime >= appointmentData.endTime) {
        toast({
          title: "Validation Error",
          description: "End time must be after start time",
          variant: "destructive"
        });
        return;
      }
    }

    setIsSaving(true);
    try {
      // ALWAYS use patientClinicDataId directly - it's set to the current patient ID 
      // when patient data is loaded (from visiblePatient API enrollmentPatientId for ES users)
      // This ensures we always use the most up-to-date patient ID
      let patientIdToUse = patientClinicDataId;
      let workflowIdToUse = externalWorkflowId || null;

      // For ES users, get workflowId from assignmentData
      if (user?.type === 'ES' && assignmentData?.workflowId) {
        workflowIdToUse = assignmentData.workflowId;
      }
      
      console.log('[useESPatientOperations] Using patientId for save:', patientIdToUse, 'workflowId:', workflowIdToUse);

      // Build programs array from selected statuses
      console.log('[useESPatientOperations] Building programs array with:', {
        selectedStatusesByProgramType,
        statusToIdMapping,
        programTypeStatuses: programTypeStatuses?.length
      });

      // Validate that at least one enrollment status is selected
      if (!selectedStatusesByProgramType || Object.keys(selectedStatusesByProgramType).length === 0) {
        setIsSaving(false);
        toast({
          title: "Validation Error",
          description: "Please select at least one enrollment status before saving",
          variant: "destructive"
        });
        return;
      }

      const programs = Object.entries(selectedStatusesByProgramType).map(([programType, statusName]) => {
        // Get program_status_id from status name
        const programStatusId = statusToIdMapping[statusName];
        if (!programStatusId) {
          throw new Error(`Invalid enrollment status selected: ${statusName}`);
        }

        // Get program_type_id from programTypeStatuses
        const programTypeInfo = programTypeStatuses?.find(
          pts => pts.program_type === programType && pts.program_status === statusName
        );
        
        if (!programTypeInfo?.program_type_id) {
          throw new Error(`Cannot find program type ID for: ${programType}`);
        }

        // Build program update object with appointment data if provided
        const programUpdate: any = {
          programTypeId: programTypeInfo.program_type_id,
          programStatusId: programStatusId,
          workflowStatusId: workflowStatusId,
          currentWorkflowId: workflowIdToUse,
          appointmentId: appointmentId,
          communicationTypeId: null,
          communicationAttempt: null,
          comment: comment.trim(),
          groupId: groupId || null
        };

        // Add appointment fields if appointment data is provided
        if (appointmentData) {
          // appointmentType: 1 = Virtual, 2 = In-Person
          programUpdate.appointmentType = appointmentData.appointmentType === 'virtual' ? 1 : 
                                          appointmentData.appointmentType === 'in-person' ? 2 : undefined;
          programUpdate.appointmentDate = appointmentData.date; // YYYY-MM-DD format
          // Format times with seconds for API (HH:mm:ss)
          programUpdate.appointmentStartTime = appointmentData.startTime.includes(':') && appointmentData.startTime.split(':').length === 2 
            ? `${appointmentData.startTime}:00` 
            : appointmentData.startTime;
          programUpdate.appointmentEndTime = appointmentData.endTime.includes(':') && appointmentData.endTime.split(':').length === 2 
            ? `${appointmentData.endTime}:00` 
            : appointmentData.endTime;
          // Set appointmentTime same as start time
          programUpdate.appointmentTime = programUpdate.appointmentStartTime;
          
          // Virtual appointments use resourceId, in-person use facilityId
          if (appointmentData.appointmentType === 'virtual') {
            programUpdate.resourceId = appointmentData.resourceId;
            programUpdate.resourceTimezone = appointmentData.timezone;
            programUpdate.resourceAvailabilityDayTimeId = appointmentData.availabilityDayTimeId;
          } else if (appointmentData.appointmentType === 'in-person') {
            programUpdate.facilityAvailabilityDayTimeId = appointmentData.availabilityDayTimeId;
          }
        }

        return programUpdate;
      });

      // Final validation - ensure programs array is not empty
      if (programs.length === 0) {
        setIsSaving(false);
        toast({
          title: "Validation Error",
          description: "No valid enrollment status found. Please select an enrollment status.",
          variant: "destructive"
        });
        return;
      }

      // Build the payload structure matching PatientEnrollmentUpdateDto
      const payload = {
        programs: programs
      };

      console.log('[useESPatientOperations] Save payload:', payload, 'userType:', user?.type);

      // Use patient_id for both URL and payload
      const success = await esService.savePatientData(payload, patientIdToUse);

      if (success) {
        // After successful save, get group options and validate stored group
        const groupOptions = await esService.loadGroupOptions();
        
        // Check if stored group is still valid (but don't show alert yet)
        if (user?.id) {
          const stored = loadGroupSelection(user.id);
          if (stored) {
            const storedGroupExists = groupOptions.some(g => g.group_id === stored.groupId);
            if (!storedGroupExists) {
              // Stored group no longer exists, store this info for next patient click
              console.warn(`Stored group ${stored.groupId} (${stored.groupName}) no longer exists after save`);
              setStoredGroupInvalid(true);
              setInvalidGroupName(stored.groupName);
              sessionStorage.removeItem(STORAGE_KEY);
            } else {
              // Stored group is still valid
              setStoredGroupInvalid(false);
              setInvalidGroupName('');
            }
          }
        }

        toast({
          title: "Success",
          description: "Patient enrollment saved successfully"
        });
        setHasUnsavedChanges(false);
        setSelectedCCMStatus('');
        setComment('');
        setAppointmentDateTime('');
        setSelectedWorkflowStatusId(null); // Reset workflow status

        // Refetch ONLY enrollment details (no assignment query) to show updated enrollment attempts
        await refetchPatientData();

        // Enable the next patient button and mark patient as saved
        setNextButtonDisabled(false);
        setIsPatientSaved(true);
        setIsFormLocked(true); // Lock form after successful save
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save patient data",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle transfer patient
  const handleTransferPatient = async (
    patientData: any,
    patientClinicDataId: number,
    groupId: number,
    ccmStatus?: string,
    transferComment?: string
  ) => {
    if (transferButtonDisabled || isTransferInProgress) {
      return;
    }

    // Use provided parameters or fall back to internal state
    const effectiveCCMStatus = ccmStatus || selectedCCMStatus;
    const effectiveComment = transferComment || comment;

    if (effectiveCCMStatus !== 'Consent') {
      toast({
        title: "Transfer Not Allowed",
        description: "Patient can only be transferred with Consent status",
        variant: "destructive"
      });
      return;
    }

    if (!effectiveComment.trim()) {
      toast({
        title: "Comment Required",
        description: "Please add a comment before transferring patient",
        variant: "destructive"
      });
      return;
    }

    if (!patientData || !user?.id || !token || !esService) {
      toast({
        title: "Error",
        description: "Missing required data for transfer",
        variant: "destructive"
      });
      return;
    }

    // Immediately disable the button to prevent multiple clicks
    setTransferButtonDisabled(true);
    setIsTransferInProgress(true);

    try {
      // Fetch fresh patient data to ensure we have the correct provider_id
      const result = await esService.getPatientData();
      if (!result?.patientData) {
        throw new Error('Failed to fetch current patient data for transfer');
      }
      const freshPatientData = result.patientData;

      const transferPayload = {
        global_flag: false,
        comment: effectiveComment.trim(),
        pep_patient_id: patientClinicDataId || 0,
        provider_id: freshPatientData.provider_id,
        emr_id: freshPatientData.emr_id || "",
        first_name: freshPatientData.first_name,
        last_name: freshPatientData.last_name,
        address_line_1: freshPatientData.address_line_1,
        city: freshPatientData.city,
        state: freshPatientData.state,
        zip_code: freshPatientData.zip_code,
        email_address: freshPatientData.email,
        home_number: freshPatientData.home_phone_number,
        mobile_number: freshPatientData.mobile_phone_number,
        primary_insurance: freshPatientData.primary_insurance_provider,
        primary_insurance_number: freshPatientData.primary_insurance_id,
        secondary_insurance: freshPatientData.secondary_insurance_provider,
        secondary_insurance_number: freshPatientData.secondary_insurance_id,
        program_eligibility: ["CCM"],
        group_id: groupId || 41,
        deferred_queue: false
      };

      const transferResult = await esService.transferPatient(transferPayload, patientClinicDataId);

      if (transferResult.success && transferResult.transfer_id) {
        setTransferId(transferResult.transfer_id);
        setShowTransferStatus(true);
        setTransferState('waiting');

        // Disable all buttons when transfer is initiated
        setSaveButtonDisabled(true);
        setTransferButtonDisabled(true);
        setNextButtonDisabled(true);

        // Initialize WebSocket subscription using proper service
        initializeTransferWebSocket(transferResult.transfer_id);

        // Set timeout for transfer
        transferTimeoutRef.current = setTimeout(async () => {

          // Stop transfer monitoring using proper service
          transferWebSocketService.stopTransferMonitoring();

          // Call invalidate transfer API
          if (transferResult.transfer_id && user?.id && token && esService) {
            try {
              await esService.invalidateTransfer(transferResult.transfer_id);
            } catch (error) {
              console.error('Error invalidating transfer on timeout:', error);
            }
          }

          // Update UI state
          setShowTransferStatus(false);
          setTransferState('idle');
          setSaveButtonDisabled(false);
          // Reset transfer button based on current CCM status
          setTransferButtonDisabled(selectedCCMStatus !== 'Consent');
          setIsTransferInProgress(false);
          setNextButtonDisabled(true);

          toast({
            title: "Transfer Timeout",
            description: "Transfer request timed out. You can try again.",
            variant: "destructive"
          });
        }, 120000); // 2 minutes

        setTransferTimeout(transferTimeoutRef.current);
      } else {
        setTransferButtonDisabled(false);
        setIsTransferInProgress(false);
        // Toast notification removed - transfer status popup provides sufficient feedback
      }
    } catch (error) {
      console.error('Error transferring patient:', error);
      setTransferButtonDisabled(false);
      setIsTransferInProgress(false);
      toast({
        title: "Transfer Failed",
        description: "Failed to transfer patient. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Initialize WebSocket for transfer using proper service architecture
  const initializeTransferWebSocket = (transferId: number) => {
    if (!hasuraToken) {
      toast({
        title: "Authentication Error",
        description: "Session expired. Please refresh the page and try again.",
        variant: "destructive"
      });
      return;
    }

    console.log('Starting transfer monitoring for transfer ID:', transferId);
    
    // Use the proper WebSocket service instead of manual WebSocket creation
    transferWebSocketService.startTransferMonitoring({
      transferId,
      hasuraToken,
      onTransferComplete: (careSpecialistName: string) => {
        
        // Clear the 2-minute timeout to prevent timeout error after successful transfer
        if (transferTimeoutRef.current) {
          clearTimeout(transferTimeoutRef.current);
          transferTimeoutRef.current = null;
        }

        handleTransferAccepted(careSpecialistName);

        // Close popup after 3 seconds
        setTimeout(() => {
          setShowTransferStatus(false);
          setTransferState('idle');
        }, 3000);
      },
      onError: (error: string) => {
        setIsTransferInProgress(false);
        
        if (error.includes('JWT') || error.includes('invalid-jwt') || error.includes('Session')) {
          toast({
            title: "Session Expired",
            description: "Your session has expired. Please refresh the page and try again.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Connection Error",
            description: "Failed to monitor transfer status. Please try again.",
            variant: "destructive"
          });
        }
      },
      timeout: 120000 // 2 minutes
    }).catch((error) => {
      setIsTransferInProgress(false);
      toast({
        title: "Connection Error",
        description: "Failed to establish transfer monitoring. Please try again.",
        variant: "destructive"
      });
    });
  };

  // Handle transfer accepted
  const handleTransferAccepted = (careSpecialistName: string) => {
    setCareSpecialist(careSpecialistName);
    setTransferState('accepted');
    setTransferCompleted(true);

    // Clear the transfer timeout
    if (transferTimeout) {
      clearTimeout(transferTimeout);
      setTransferTimeout(null);
    }

    // Clear unsaved changes and enable Next Patient button
    setHasUnsavedChanges(false);
    setSaveButtonDisabled(true);
    setTransferButtonDisabled(true);
    setNextButtonDisabled(false);
    setIsFormLocked(true); // Lock form after successful transfer

    // Toast notification removed - transfer status popup provides sufficient feedback
  };

  // Handle close transfer status
  const handleCloseTransferStatus = async (isCancellation: boolean = true) => {
    // Only call invalidate transfer API if it's a cancellation and we have a transfer ID
    if (isCancellation && transferId && token && user?.id && esService) {
      try {
        await esService.invalidateTransfer(transferId);
        toast({
          title: "Transfer Cancelled",
          description: "Patient transfer has been cancelled"
        });
      } catch (error) {
        console.error('Error invalidating transfer:', error);
      }
    }

    setShowTransferStatus(false);
    setTransferId(null);
    setTransferState('idle');

    // Close WebSocket if still open (but don't close on cancellation - let it stay open)
    if (!isCancellation) {
      transferWebSocketService.stopTransferMonitoring();
    }

      // Clear timeout
    if (transferTimeout) {
      clearTimeout(transferTimeout);
      setTransferTimeout(null);
    }

    // Clear transferTimeoutRef as well
    if (transferTimeoutRef.current) {
      clearTimeout(transferTimeoutRef.current);
      transferTimeoutRef.current = null;
    }

    // Handle button states based on whether it was cancelled or completed
    if (isCancellation) {
      setSaveButtonDisabled(false);
      // Reset transfer button state - it should be enabled if CCM status is Consent
      // Always reset transfer progress state on cancellation
      setIsTransferInProgress(false);
      setTransferButtonDisabled(selectedCCMStatus !== 'Consent');
      setNextButtonDisabled(true);
    }
  };

  /**
   * Save patient demographic changes
   */
  const handleSaveDemographic = async (
    patientData: any,
    editableFields: any,
    patientId: number | null,
    selectedEligibility?: string[]
  ) => {
    if (!user?.id || !patientId) {
      toast({
        title: "Error",
        description: "Missing required patient information",
        variant: "destructive"
      });
      return;
    }

    setIsSavingDemographic(true);
    try {
      // Helper to check if email is valid
      const isValidEmail = (email: string): boolean => {
        return email && 
               email.trim() !== '' &&
               email !== 'n/a' && 
               email !== 'N/A' && 
               !email.toLowerCase().includes('not available');
      };

      // Build the payload matching the API structure - all fields are optional
      const payload: any = {
        firstName: editableFields.first_name || "",
        middleName: editableFields.middle_name || "",
        lastName: editableFields.last_name || "",
        dob: editableFields.dob || "",
        gender: editableFields.gender || "",
        addressLineOne: editableFields.address_line_1 || "",
        addressLineTwo: editableFields.address_line_2 || "",
        city: editableFields.city || "",
        state: editableFields.state || "",
        zipCode: editableFields.zip_code || "",
        mobileNumber: editableFields.mobile_phone_number || "",
        homeNumber: editableFields.home_phone_number || "",
        emergencyContactName: patientData.emergency_contact_name || "",
        emergencyContactPhone: patientData.emergency_contact_phone || "",
        insuranceProvider: editableFields.primary_insurance_provider || "",
        insurancePolicyNumber: editableFields.primary_insurance_id || "",
        medicalRecordNumber: patientData.medical_record_number || "",
        isActive: true,
        primaryInsuranceProvider: editableFields.primary_insurance_provider || "",
        primaryInsurancePolicyNumber: editableFields.primary_insurance_id || "",
        secondaryInsuranceProvider: editableFields.secondary_insurance_provider || "",
        secondaryInsurancePolicyNumber: editableFields.secondary_insurance_id || "",
        primaryDx: editableFields.primary_dx || null,
        secondaryDx: editableFields.secondary_dx || null,
        providerName: editableFields.provider_name || "",
        facilityName: editableFields.facility_name || "",
        emrId: editableFields.emr_id || "",
        clinicId: editableFields.clinic_id || patientData.clinic_id || null,
        clinicFacilityId: editableFields.facility_id || patientData.clinic_facility_id || null,
        providerId: editableFields.provider_id || patientData.provider_id || null
      };

      // Only include email if it's valid
      if (isValidEmail(editableFields.email)) {
        payload.email = editableFields.email;
      }

      // Only include EMR fields if they exist
      if (patientData.emr_patient_master_id) {
        payload.emrPatientMasterId = patientData.emr_patient_master_id;
      }
      if (patientData.emr_name) {
        payload.emrName = patientData.emr_name;
      }

      // Include program eligibility if provided
      if (selectedEligibility) {
        payload.isCcmEligible = selectedEligibility.includes('CCM');
        payload.isRpmEligible = selectedEligibility.includes('RPM');
        payload.isPcmEligible = selectedEligibility.includes('PCM');
      }

      const response = await patientsApi.updatePatientDemographic(
        user.id,
        patientId,
        payload,
        token
      );

      if (response.success) {
        toast({
          title: "Success",
          description: "Patient demographic information updated successfully",
          duration: 1000
        });
        return true;
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to update patient demographic",
          variant: "destructive",
          duration: 1000
        });
        return false;
      }
    } catch (error) {
      console.error('Error updating patient demographic:', error);
      toast({
        title: "Error",
        description: "Failed to update patient demographic information",
        variant: "destructive",
        duration: 1000
      });
      return false;
    } finally {
      setIsSavingDemographic(false);
    }
  };

  // Handle next patient
  const handleNextPatient = async (
    groupId: number,
    refetchPatientData: (patientId?: number) => Promise<void>,
    initializeEditableFields: (data: any) => void,
    setPatientClinicDataId: (id: number) => void,
    setGroupId: (id: number) => void,
    setCCMStatusUpdates: (updates: any[]) => void,
    setPatientData?: (data: any) => void,
    currentPatientId?: number | null,
    setCurrentPatientId?: (id: number) => void,
    setClinicNumbers?: (numbers: any[]) => void,
    setClinicNumbersSources?: (sources: any[]) => void,
    setSmsClinicNumbers?: (numbers: any[]) => void,
    setSmsNumbersSources?: (sources: any[]) => void,
    headerRefetchMethods?: {
      refetchSMSCount: () => Promise<void>;
      refetchVoicemailCount: () => Promise<void>;
    },
    resetAppointmentData?: () => void
  ) => {
    if (hasUnsavedChanges) {
      toast({
        title: "Unsaved Changes",
        description: "Please save your changes before proceeding to next patient",
        variant: "destructive"
      });
      return;
    }

    // Close WebSocket connection when moving to next patient (transfer is need-based)
    transferWebSocketService.stopTransferMonitoring();

    // Clear any remaining timeouts
    if (transferTimeoutRef.current) {
      clearTimeout(transferTimeoutRef.current);
      transferTimeoutRef.current = null;
    }

    // Reset transfer state completely for new patient
    setShowTransferStatus(false);
    setTransferId(null);
    setTransferState('idle');
    setCareSpecialist('');
    setTransferCompleted(false);
    setIsTransferInProgress(false);

    await confirmNextPatient(
      groupId,
      refetchPatientData,
      initializeEditableFields,
      setPatientClinicDataId,
      setGroupId,
      setCCMStatusUpdates,
      setPatientData,
      currentPatientId,
      setCurrentPatientId,
      setClinicNumbers,
      setClinicNumbersSources,
      setSmsClinicNumbers,
      setSmsNumbersSources,
      headerRefetchMethods,
      resetAppointmentData
    );
  };

  // Confirm next patient with sequential loading
  const confirmNextPatient = async (
    groupId: number,
    refetchPatientData: (patientId?: number) => Promise<void>,
    initializeEditableFields: (data: any) => void,
    setPatientClinicDataId: (id: number) => void,
    setGroupId: (id: number) => void,
    setCCMStatusUpdates: (updates: any[]) => void,
    setPatientData?: (data: any) => void,
    currentPatientId?: number | null,
    setCurrentPatientId?: (id: number) => void,
    setClinicNumbers?: (numbers: any[]) => void,
    setClinicNumbersSources?: (sources: any[]) => void,
    setSmsClinicNumbers?: (numbers: any[]) => void,
    setSmsNumbersSources?: (sources: any[]) => void,
    headerRefetchMethods?: {
      refetchSMSCount: () => Promise<void>;
      refetchVoicemailCount: () => Promise<void>;
    },
    resetAppointmentData?: () => void
  ) => {
    if (!esService) return;

    // Step 1: Display loading indicator and lock form
    setIsLoadingNext(true);
    setSaveButtonDisabled(true);
    setTransferButtonDisabled(true);
    setNextButtonDisabled(true);

    // Reset sequential loading states will be handled by resetFormData call
    // NOTE: Keep current patient data visible until next patient loads - don't clear patientData or editableFields here

    try {
      // Step 2: Clear stored group invalid state (no alert needed - group switch popup will handle this)
      if (storedGroupInvalid && invalidGroupName) {
        console.log(`Stored group "${invalidGroupName}" is no longer available - proceeding to group switch popup`);
        setStoredGroupInvalid(false);
        setInvalidGroupName('');
      }

      // Step 3: Simply get next patient from current group - no other APIs needed
      if (!groupId) {
        // No group ID available - show congratulations message
        if (setPatientData) {
          setPatientData(null);
        }
        
        // Unlock form but keep disabled state
        setSaveButtonDisabled(false);
        setTransferButtonDisabled(true);
        setNextButtonDisabled(true);
        return;
      }

      // IMPORTANT: Refetch header data (SMS count, voicemail count) for fresh counts
      if (headerRefetchMethods) {
        try {
          await Promise.all([
            headerRefetchMethods.refetchSMSCount(),
            headerRefetchMethods.refetchVoicemailCount()
          ]);
          console.log('[confirmNextPatient] Header data refreshed (SMS & voicemail counts)');
        } catch (error) {
          console.error('[confirmNextPatient] Error refreshing header data:', error);
          // Don't block patient loading if header refresh fails
        }
      }

      // Get next patient from the current group using workflow assignments
      // This calls both APIs: visiblePatient and enrollmentDetails
      const patientResult = await esService.getPatientData();
      console.log('[confirmNextPatient] Full result from getPatientData:', patientResult);
      
      if (patientResult?.patientData && patientResult?.assignmentData) {
        const actualPatientData = patientResult.patientData;
        // Use enrollmentPatientId and userGroupId from visiblePatient API
        const pep_patient_clinic_data_id = patientResult.assignmentData.enrollmentPatientId;
        const group_id = patientResult.assignmentData.userGroupId;
        
        console.log('[confirmNextPatient] Patient data received:', {
          patientId: pep_patient_clinic_data_id,
          groupId: group_id,
          hasEnrollmentAttempts: !!actualPatientData.enrollmentAttemptsByProgramType,
          enrollmentKeys: actualPatientData.enrollmentAttemptsByProgramType ? Object.keys(actualPatientData.enrollmentAttemptsByProgramType) : [],
          fullData: actualPatientData
        });

        // Update state with new patient
        setPatientClinicDataId(pep_patient_clinic_data_id);
        setGroupId(group_id);
        
        // Track current patient ID for unlock functionality
        if (setCurrentPatientId) {
          setCurrentPatientId(pep_patient_clinic_data_id);
        }

        // No need to lock patient by group for next patient within same group
        // Lock is only needed on initial login or group change
        console.log(`Loading next patient ${pep_patient_clinic_data_id} from same group ${group_id} - no lock needed`);

        // Update patient data state with new patient (already fetched from getPatientData)
        if (setPatientData) {
          setPatientData(actualPatientData);
        }
        initializeEditableFields(actualPatientData);
        
        // Update CCM status updates from REST API response
        setCCMStatusUpdates(actualPatientData.ccm_status_updates || []);
        
        // Save locked patient info for refresh scenarios
        if (user?.id) {
          saveLockedPatient(user.id, pep_patient_clinic_data_id);
        }

        // Load clinic numbers if available (Call and SMS separately via REST API)
        if (actualPatientData.clinic_id && setClinicNumbers && setSmsClinicNumbers && setClinicNumbersSources && setSmsNumbersSources) {
          try {
            // Load call numbers
            const callNums = await esService.loadCallNumbers(actualPatientData.clinic_id);
            setClinicNumbers(callNums);
            const callSources = esService.generateSourceOptions(callNums);
            setClinicNumbersSources(callSources);
            
            // Load SMS numbers
            const smsNums = await esService.loadSmsNumbers(actualPatientData.clinic_id);
            setSmsClinicNumbers(smsNums);
            const smsSources = esService.generateSourceOptions(smsNums);
            // Add "All" option for SMS
            if (!smsSources.find(s => s.number_source_id === 0)) {
              smsSources.unshift({ number_source: 'All', number_source_id: 0 });
            }
            setSmsNumbersSources(smsSources);
          } catch (error) {
            console.error('Error loading clinic/SMS numbers during next patient:', error);
          }
        }

        // Step 3: Reset form state and unlock form (after data is loaded)
        setSelectedCCMStatus('');
        setComment('');
        setAppointmentDateTime('');
        if (resetAppointmentData) resetAppointmentData();
        setSelectedWorkflowStatusId(null); // Reset workflow status
        setHasUnsavedChanges(false);
        setIsPatientSaved(false);
        setIsFormLocked(false); // Unlock form for new patient

        // Ensure transfer state is completely reset
        setTransferCompleted(false);
        setIsTransferInProgress(false);
        setTransferState('idle');

        // Unlock form controls after data is fully loaded
        setSaveButtonDisabled(false);
        setTransferButtonDisabled(true);
        setNextButtonDisabled(true);

        toast({
          title: "Next Patient Loaded",
          description: "Successfully loaded next patient data"
        });

        // Step 4: Scroll to top of page to display patient details
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // No next patient in current group - show group completion dialog
        // Check if we're in deferred mode by looking for a custom parameter
        const isDeferredMode = window.location.href.includes('deferred=true') ||
                              document.querySelector('[data-deferred-mode="true"]') !== null;
        
        if (isDeferredMode) {
          // In deferred mode, don't show group completion dialog
          // Just show empty state message
          toast({
            title: "No More Patients",
            description: "You've completed all deferred patients."
          });
          
          // Signal to the parent component that there are no more patients
          // This will trigger the empty state in PatientEnrollmentForm
          if (setPatientData) {
            setPatientData(null);
          }
          
          // Unlock form but keep disabled state
          setSaveButtonDisabled(false);
          setTransferButtonDisabled(true);
          setNextButtonDisabled(true);
        } else {
          // In normal mode, show group completion dialog (already implemented)
          setShowGroupCompletionDialog(true);
          
          // Unlock form but keep disabled state
          setSaveButtonDisabled(false);
          setTransferButtonDisabled(true);
          setNextButtonDisabled(true);
        }
      }
    } catch (error) {
      console.error('Error loading next patient:', error);

      // On error, unlock form but keep disabled state
      setSaveButtonDisabled(false);
      setTransferButtonDisabled(true);
      setNextButtonDisabled(true);

      toast({
        title: "Error",
        description: "Failed to load next patient data",
        variant: "destructive"
      });
    } finally {
      // Always clear loading state last
      setIsLoadingNext(false);
    }
  };

  // Handle group selection change with enhanced loading states and error handling
  const handleGroupSelectionChange = async (
    newGroupId: string,
    patientClinicDataId: number | null,
    refetchPatientData: (patientId?: number) => Promise<void>,
    setPatientClinicDataId: (id: number) => void,
    setGroupId: (id: number) => void,
    setSelectedGroup: (group: string) => void,
    setIsLoadingGroupChange: (loading: boolean) => void,
    initializeEditableFields: (data: any) => void,
    setCCMStatusUpdates: (updates: any[]) => void,
    setPersistentGroup?: (groupId: number, groupName: string) => void,
    groupOptions?: Array<{ group_id: number; group_name: string }>,
    conditionalLockPatientByGroup?: (groupId: number, isGroupChange?: boolean) => Promise<boolean>,
    currentPatientId?: number | null,
    setCurrentPatientId?: (id: number) => void,
    setClinicNumbers?: (numbers: any[]) => void,
    setClinicNumbersSources?: (sources: any[]) => void,
    setSmsClinicNumbers?: (numbers: any[]) => void,
    setSmsNumbersSources?: (sources: any[]) => void
  ) => {
    if (!esService || !user?.id || !token) {
      toast({
        title: "Authentication Error",
        description: "Please refresh the page and try again",
        variant: "destructive"
      });
      return;
    }

    // Validate that the new group exists in available options
    if (groupOptions && groupOptions.length > 0) {
      const newGroupIdNumber = parseInt(newGroupId);
      const groupExists = groupOptions.some(g => g.group_id === newGroupIdNumber);
      
      if (!groupExists) {
        toast({
          title: "Invalid Group Selection",
          description: "The selected group is no longer available. Please select a different group.",
          variant: "destructive"
        });
        return;
      }
    }

    // Start loading state
    setIsLoadingGroupChange(true);

    // Clear patient data immediately to prevent showing misleading information
    setPatientClinicDataId(0);
    // Initialize editable fields with empty values
    initializeEditableFields({});
    // Clear CCM status updates
    setCCMStatusUpdates([]);

    try {
      const newGroupIdNumber = parseInt(newGroupId);

      // Step 1: Use conditional locking which handles unlock and lock logic
      if (conditionalLockPatientByGroup) {
        // This will unlock current patient if needed and lock new group (since it's a group change)
        const lockSuccess = await conditionalLockPatientByGroup(newGroupIdNumber, true);
        if (!lockSuccess) {
          throw new Error('Failed to lock new patient group');
        }
      } else {
        // This should never happen since conditional function is always provided
        throw new Error('Conditional lock function not available');
      }

      // Step 3: Get first patient from the newly locked group using workflow assignments
      const patientResult = await esService.getPatientData();

      if (!patientResult?.patientData || !patientResult?.assignmentData) {
        throw new Error('No patients found in the selected group');
      }

      const newPatientId = patientResult.assignmentData.enrollmentPatientId;
      const newGroupIdFromResponse = patientResult.assignmentData.userGroupId;

      // Step 4: Set patient data from the result
      const patientDataFromResult = patientResult.patientData;

      if (!patientResult?.patientData) {
        throw new Error('Failed to load patient data');
      }

      const actualPatientData = patientResult.patientData;

      // Update all state with new patient information
      setPatientClinicDataId(newPatientId);
      setGroupId(newGroupIdFromResponse);
      setSelectedGroup(newGroupId);
      
      // Track current patient ID for unlock functionality
      if (setCurrentPatientId) {
        setCurrentPatientId(newPatientId);
      }

      // Save to persistent storage if functions are provided
      if (setPersistentGroup && groupOptions) {
        const selectedGroupData = groupOptions.find(g => g.group_id === newGroupIdFromResponse);
        if (selectedGroupData) {
          setPersistentGroup(newGroupIdFromResponse, selectedGroupData.group_name);
        }
      }

      // Initialize editable fields with new patient data
      initializeEditableFields(actualPatientData);

      // Update CCM status updates from REST API response
      setCCMStatusUpdates(actualPatientData.ccm_status_updates || []);
      
      // Save locked patient info for refresh scenarios
      if (user?.id) {
        saveLockedPatient(user.id, newPatientId);
      }

      // Load clinic numbers if available (Call and SMS separately via REST API)
      if (actualPatientData.clinic_id && setClinicNumbers && setSmsClinicNumbers && setClinicNumbersSources && setSmsNumbersSources) {
        try {
          // Load call numbers
          const callNums = await esService.loadCallNumbers(actualPatientData.clinic_id);
          setClinicNumbers(callNums);
          const callSources = esService.generateSourceOptions(callNums);
          setClinicNumbersSources(callSources);
          
          // Load SMS numbers
          const smsNums = await esService.loadSmsNumbers(actualPatientData.clinic_id);
          setSmsClinicNumbers(smsNums);
          const smsSources = esService.generateSourceOptions(smsNums);
          // Add "All" option for SMS
          if (!smsSources.find(s => s.number_source_id === 0)) {
            smsSources.unshift({ number_source: 'All', number_source_id: 0 });
          }
          setSmsNumbersSources(smsSources);
        } catch (error) {
          console.error('Error loading clinic/SMS numbers during group change:', error);
        }
      }

      // Reset form state for new patient - ensure form is completely unlocked
      setSelectedCCMStatus('');
      setComment('');
      setAppointmentDateTime('');
      setSelectedWorkflowStatusId(null); // Reset workflow status
      setHasUnsavedChanges(false);
      setIsPatientSaved(false); // Reset saved state to allow editing
      setIsFormLocked(false); // Unlock form for new patient from group change
      setTransferCompleted(false); // Reset transfer completed state
      setIsTransferInProgress(false);
      setTransferState('idle');

      // Reset button states
      setSaveButtonDisabled(false);
      setTransferButtonDisabled(true);
      setNextButtonDisabled(true);

      // No need to refetch patient data as we already have it loaded above
      // This prevents duplicate API calls

      toast({
        title: "Group Changed Successfully",
        description: `Switched to group ${newGroupId} - First patient loaded`,
      });

    } catch (error) {
      console.error('Error changing group:', error);

      // Provide specific error messages based on the error
      let errorMessage = "Failed to change group";
      if (error instanceof Error) {
        if (error.message.includes('unlock')) {
          errorMessage = "Failed to unlock current patient";
        } else if (error.message.includes('lock')) {
          errorMessage = "Failed to lock new patient group";
        } else if (error.message.includes('No patients')) {
          errorMessage = "No patients available in the selected group";
        } else if (error.message.includes('patient data')) {
          errorMessage = "Failed to load patient information";
        }
      }

      toast({
        title: "Group Change Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      // Always clear loading state
      setIsLoadingGroupChange(false);
    }
  };

  // Form reset functionality for complete data clearing
  const resetFormState = () => {
    setSelectedCCMStatus('');
    setComment('');
    setAppointmentDateTime('');
    setSelectedWorkflowStatusId(null); // Reset workflow status
    setHasUnsavedChanges(false);
    setIsPatientSaved(false);
    setIsFormLocked(false);
    setTransferCompleted(false);
    setIsTransferInProgress(false);
    setTransferState('idle');
    setSaveButtonDisabled(false);
    setTransferButtonDisabled(true);
    setNextButtonDisabled(true);
  };

  // Handle group completion dialog confirmation
  const handleGroupCompletionConfirm = () => {
    setShowGroupCompletionDialog(false);
    // This will be handled by the component calling switchToNextAvailableGroup
  };

  return {
    // Form state
    selectedCCMStatus,
    comment,
    appointmentDateTime,
    hasUnsavedChanges,
    isFormLocked,
    selectedWorkflowStatusId,

    // Button states
    isSaving,
    isSavingDemographic,
    saveButtonDisabled,
    transferButtonDisabled,
    nextButtonDisabled,
    isLoadingNext,
    isPatientSaved,

    // Transfer state
    showTransferStatus,
    transferState,
    transferId,
    careSpecialist,
    transferCompleted,
    isTransferInProgress,

    // SMS state
    smsDialogOpen,
    selectedPhoneNumber,
    selectedPhoneType,

    // Group completion dialog state
    showGroupCompletionDialog,
    currentGroupName,
    nextGroupName,

    // Actions
    handleCCMStatusChange,
    handleCommentChange,
    handleSave,
    handleSaveDemographic,
    handleTransferPatient,
    handleTransferAccepted,
    handleCloseTransferStatus,
    handleNextPatient,
    handleGroupSelectionChange,
    resetFormState,
    handleGroupCompletionConfirm,

    // Setters
    setSelectedCCMStatus,
    setComment,
    setAppointmentDateTime,
    setHasUnsavedChanges,
    setSelectedWorkflowStatusId,
    setSmsDialogOpen,
    setSelectedPhoneNumber,
    setSelectedPhoneType,
    setNextButtonDisabled,
    setSaveButtonDisabled,
    setTransferButtonDisabled,
    setIsFormLocked,
    setShowGroupCompletionDialog,
    setCurrentGroupName,
    setNextGroupName,

    // Refs
    transferTimeoutRef
  };
};